import { useState } from 'react';
import type { PublicarDto } from '@triade/contracts';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { formatarContagem, formatarData } from '@/lib/format';

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

/** Data civil local (YYYY-MM-DD). UTC virava o dia seguinte depois das 21h de Brasilia e a vigencia caia no futuro. */
const hoje = () => new Date().toLocaleDateString('en-CA');

/** "dd/mm" de uma data ISO (so reordena os campos; a API e quem valida). */
const diaMes = (iso: string) => formatarData(iso).slice(0, 5);

/**
 * Situacao resultante da vigencia digitada. Texto so de datas: nada aqui decide se a publicacao vale (isso e da API).
 * Com fim, e promocao: depois do fim volta a valer a lista anterior.
 */
export function textoSituacaoVigencia(inicio: string, fim: string, minimo: string): string {
  if (fim !== '') return `Promoção de ${diaMes(inicio)} a ${diaMes(fim)}: depois volta a valer a lista anterior`;
  if (inicio === minimo) return 'Entra em vigor hoje';
  return `Programada para ${diaMes(inicio)}`;
}

interface PublicarDialogProps {
  aberto: boolean;
  onAberto: (v: boolean) => void;
  total: number;
  pendente: boolean;
  onPublicar: (dto: PublicarDto) => Promise<void>;
}

/**
 * Dialog "Publicar lista": vigencia a partir de (obrigatoria, >= hoje, padrao hoje), fim opcional (vazio = indeterminada,
 * inclusive no ultimo dia) e observacao. Erros da API (ex.: VIGENCIA_RETROATIVA, VIGENCIA_INVALIDA) aparecem aqui como vieram.
 * Usado pela Liberacao de preco (item e lote). A Lista de precos nao publica nada (so consulta).
 */
export function PublicarDialog({ aberto, onAberto, total, pendente, onPublicar }: PublicarDialogProps) {
  const [inicio, setInicio] = useState(hoje);
  const [fim, setFim] = useState('');
  const [observacao, setObservacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const minimo = hoje();

  const inicioInvalido = inicio === '' || inicio < minimo;
  const fimInvalido = fim !== '' && fim < inicio;

  function abrir(v: boolean) {
    if (v) {
      setInicio(hoje());
      setFim('');
      setObservacao('');
      setErro(null);
    }
    onAberto(v);
  }

  async function enviar(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    setErro(null);
    try {
      const obs = observacao.trim();
      await onPublicar({
        validFrom: inicio,
        ...(fim !== '' ? { validAte: fim } : {}),
        ...(obs ? { observacao: obs } : {}),
      });
      onAberto(false);
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={abrir}>
      <DialogContent>
        <form onSubmit={(ev) => void enviar(ev)} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Publicar lista</DialogTitle>
            <DialogDescription>
              Publica os {formatarContagem(total)} preços do rascunho. Depois de publicada, a lista não é alterada: a próxima publicação vale a partir da sua vigência.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="publicar-inicio">Vigência a partir de</Label>
            <Input id="publicar-inicio" type="date" min={minimo} value={inicio} onChange={(ev) => setInicio(ev.target.value)} required aria-invalid={inicioInvalido || undefined} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="publicar-fim">Até (opcional)</Label>
            <Input id="publicar-fim" type="date" min={inicio || minimo} value={fim} onChange={(ev) => setFim(ev.target.value)} aria-invalid={fimInvalido || undefined} aria-describedby="publicar-fim-ajuda" />
            <span id="publicar-fim-ajuda" className="text-caption text-ink-muted">
              Deixe vazio para vigência indeterminada. O último dia conta (inclusive).
            </span>
            {fimInvalido && <span className="text-caption text-danger-ink">O fim da vigência não pode ser anterior ao início.</span>}
          </div>
          <p data-testid="publicar-situacao" className="rounded-xl border border-border bg-surface-muted px-3 py-2 text-small text-ink-secondary">
            {inicioInvalido ? 'Escolha a data de início da vigência (hoje ou depois).' : textoSituacaoVigencia(inicio, fim, minimo)}
          </p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="publicar-observacao">Observação (opcional)</Label>
            <Input id="publicar-observacao" maxLength={300} value={observacao} onChange={(ev) => setObservacao(ev.target.value)} />
          </div>
          {erro && (
            <p role="alert" className="text-small text-danger-ink">
              {erro}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onAberto(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={pendente || total === 0 || inicioInvalido || fimInvalido}>
              Publicar lista, vigente a partir de {diaMes(inicio)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
